// ═══════════════════════════════════════════════════════════════
// VariableInsertPicker.tsx – „Változó” gomb egy szövegmező mellé:
// lenyíló lista gyorskeresővel (megnevezés, kulcs vagy érték szerint), a kiválasztott
// változó ÉRTÉKE a kurzor helyére kerül. Billentyűzet: ↑/↓, Enter, Esc.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Braces, Search, Settings2, RefreshCw } from 'lucide-react';
import { useTemplateVariables, type TemplateVariableItem } from '../../hooks/useTemplateVariables';
import { useContentSnippets } from '../../hooks/useContentSnippets';
import { TemplateVariablesManagerModal } from '../datahub/TemplateVariablesManagerModal';
import { ApiClient } from '../../api/client';
import type { LinkedEntity, LinkedEntityType, OrgUnit } from '../../types';
import { ENTITY_TYPE_GROUP } from '../personnel/linkedModel';

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// a csoportok sorrendje a listában: elöl az élő blokkok és a tartalom-változók, utánuk a többi
const GROUP_TITLE: Record<string, string> = {
  szervezeti_blokk: 'Szervezet & személyzet (élő blokk)',
  kapcsolt_blokk: 'Kapcsolt szervek (élő blokk)',
  tartalom_valtozo: 'Tartalom-változók (teljes tartalom)',
  szervezet: 'Szervezeti alapadatok', szemelyzet: 'Tisztségviselők', felugyelet: 'Felügyelet',
  digitalis: 'Digitális azonosítók', penzugy: 'Pénzügyi azonosítók', egyedi: 'Saját változók', rendszer: 'Automatikus',
};
export const PERSONNEL_BLOCK_CATEGORY = 'szervezeti_blokk';
export const LINKED_BLOCK_CATEGORY = 'kapcsolt_blokk';
export const SNIPPET_CATEGORY = 'tartalom_valtozo';
/** tartalom-változó: a teljes (esetleg formázott) tartalom kerül beszúrásra */
export const isSnippetItem = (it: { category: string }) => it.category === SNIPPET_CATEGORY;
/** élő blokk (a bejegyzésbe hivatkozás kerül, nem érték) */
export const isLiveBlockItem = (it: { category: string }) => it.category === PERSONNEL_BLOCK_CATEGORY || it.category === LINKED_BLOCK_CATEGORY;

/** kapcsolt szervek élő blokkjai: térkép, listák típusonként, adatlapok, intézmények belső szervezete */
function linkedTokenItems(entities: LinkedEntity[]): TemplateVariableItem[] {
  const c = LINKED_BLOCK_CATEGORY;
  const it = (key: string, label: string): TemplateVariableItem => ({ key, label, value: key, category: c });
  const out: TemplateVariableItem[] = [it('{{KAPCSOLATI_TERKEP}}', 'Kapcsolati térkép – teljes'), it('{{KAPCSOLT_SZERVEK}}', 'Kapcsolt szervek – mind, típus szerint')];
  const types = [...new Set(entities.map((e) => e.entity_type))];
  for (const t of types) out.push(it(`{{KAPCSOLT_SZERVEK:${t}}}`, `Kapcsolt szervek – ${ENTITY_TYPE_GROUP[t as LinkedEntityType] || t}`));
  const sorted = [...entities].sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  for (const e of sorted) {
    out.push(it(`{{KAPCSOLT_SZERV:${e.id}}}`, `Adatlap – ${e.name}`));
    if (entities.some((x) => x.parent_entity_id === e.id)) out.push(it(`{{KAPCSOLATI_TERKEP:${e.id}}}`, `Kapcsolati térkép – ${e.name} és a hozzá kapcsolódók`));
  }
  for (const e of sorted) {
    out.push(it(`{{SZERVEZETI_DIAGRAM:intezmeny-${e.id}}}`, `Belső szervezeti diagram – ${e.name}`));
    out.push(it(`{{KAPCSOLATOK:intezmeny-${e.id}}}`, `Belső kapcsolattartók – ${e.name}`));
  }
  return out;
}

/** az élő szervezeti blokkok választható elemei: teljes szervezet + egységenként (hierarchia szerinti sorrendben) */
function personnelTokenItems(units: OrgUnit[]): TemplateVariableItem[] {
  const known = new Set(units.map((u) => u.id));
  const ordered: { u: OrgUnit; depth: number }[] = [];
  const walk = (pid: number | null, depth: number) => {
    if (depth > 15) return;
    units.filter((u) => ((u.parent_id && known.has(u.parent_id)) ? u.parent_id : null) === pid)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name, 'hu'))
      .forEach((u) => { ordered.push({ u, depth }); walk(u.id, depth + 1); });
  };
  walk(null, 0);
  const out: TemplateVariableItem[] = [
    { key: '{{SZERVEZETI_DIAGRAM}}', label: 'Szervezeti diagram – teljes szervezet', value: '{{SZERVEZETI_DIAGRAM}}', category: PERSONNEL_BLOCK_CATEGORY },
    { key: '{{KAPCSOLATOK}}', label: 'Kapcsolattartók – minden egység', value: '{{KAPCSOLATOK}}', category: PERSONNEL_BLOCK_CATEGORY },
  ];
  for (const { u, depth } of ordered) {
    const name = `${'· '.repeat(depth)}${u.name}`;
    out.push({ key: `{{SZERVEZETI_DIAGRAM:${u.id}}}`, label: `Diagram – ${name}`, value: `{{SZERVEZETI_DIAGRAM:${u.id}}}`, category: PERSONNEL_BLOCK_CATEGORY });
    out.push({ key: `{{KAPCSOLATOK:${u.id}}}`, label: `Kapcsolattartók – ${name}`, value: `{{KAPCSOLATOK:${u.id}}}`, category: PERSONNEL_BLOCK_CATEGORY });
  }
  return out;
}

/**
 * Szöveg beszúrása egy input/textarea kurzorpozíciójába.
 * Visszaadja az új értéket; a fókuszt és a kurzort a beszúrt szöveg végére állítja.
 */
export function insertAtCursor(el: HTMLInputElement | HTMLTextAreaElement | null, current: string, text: string): string {
  let start = current.length;
  let end = current.length;
  try {
    if (el && typeof el.selectionStart === 'number' && document.activeElement !== null) {
      start = el.selectionStart ?? start;
      end = el.selectionEnd ?? start;
    }
  } catch { /* number/date mezőnél nincs kijelölés */ }
  const next = current.slice(0, start) + text + current.slice(end);
  const caret = start + text.length;
  requestAnimationFrame(() => {
    if (!el) return;
    el.focus();
    try { el.setSelectionRange(caret, caret); } catch { /* */ }
  });
  return next;
}

interface Props {
  /** a cél mező (a kurzorpozícióhoz) */
  targetRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** a beszúrandó szöveg kezelése (általában: insertAtCursor + setState) */
  onInsert: (text: string, item: TemplateVariableItem) => void;
  tenantId?: number;
  label?: string;
  /** a szervezeti diagram / kapcsolattartók élő blokkjai is választhatók (csak a leírás mezőnél) */
  personnelBlocks?: boolean;
}

export const VariableInsertPicker: React.FC<Props> = ({ targetRef, onInsert, tenantId, label = 'Változó', personnelBlocks = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // a mező kijelölése a gombra kattintáskor elveszne – megjegyezzük megnyitáskor
  const selRef = useRef<{ start: number; end: number } | null>(null);
  const { items, loading, canSave } = useTemplateVariables(tenantId, open || manageOpen);
  // tartalom-változók: csak a tartalom (leírás) mezőnél
  const snippets = useContentSnippets(open && personnelBlocks, tenantId);
  const [units, setUnits] = useState<OrgUnit[] | null>(null);
  const [linked, setLinked] = useState<LinkedEntity[] | null>(null);
  useEffect(() => {
    if (!open || !personnelBlocks || units) return;
    let alive = true;
    ApiClient.getPersonnelOverview(tenantId).then((r) => { if (alive) setUnits(r.ok && r.data ? r.data.org_units || [] : []); }).catch(() => { if (alive) setUnits([]); });
    ApiClient.getLinkedEntities({ tenant_id: tenantId }).then((r) => { if (alive) setLinked(r.ok && r.data ? r.data : []); }).catch(() => { if (alive) setLinked([]); });
    return () => { alive = false; };
  }, [open, personnelBlocks, units, tenantId]);
  useEffect(() => { setUnits(null); setLinked(null); }, [tenantId]);
  const listId = useMemo(() => `varpick-${Math.random().toString(36).slice(2, 8)}`, []);

  const filtered = useMemo(() => {
    const n = norm(q.trim());
    const snippetItems: TemplateVariableItem[] = snippets.map((s) => ({
      key: `snippet:${s.id}`, label: `${s.name}${s.scope === 'global' ? '' : ' · szervezeti'}`, value: s.text || s.html.replace(/<[^>]*>/g, ' '),
      html: s.has_html ? s.html : undefined, category: SNIPPET_CATEGORY,
    }));
    const all = personnelBlocks ? [...personnelTokenItems(units || []), ...linkedTokenItems(linked || []), ...snippetItems, ...items] : items;
    const base = all.filter((i) => !n || norm(`${i.label} ${i.key} ${i.value}`).includes(n));
    // a kitöltött értékek előre, csoportonként
    const order = Object.keys(GROUP_TITLE);
    return base.sort((a, b) => (order.indexOf(a.category) - order.indexOf(b.category)) || (Number(!a.value) - Number(!b.value)));
  }, [items, q, personnelBlocks, units, linked, snippets]);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(360, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
    const up = r.bottom + 340 > window.innerHeight && r.top > 340;
    setPos({ left, top: up ? r.top - 6 : r.bottom + 6, up });
  };

  const openPicker = () => {
    const el = targetRef.current;
    try { selRef.current = el && typeof el.selectionStart === 'number' ? { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 } : null; } catch { selRef.current = null; }
    setQ(''); setActive(0); place(); setOpen(true);
  };

  useLayoutEffect(() => { if (open) requestAnimationFrame(() => searchRef.current?.focus()); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onMove = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (item: TemplateVariableItem | undefined) => {
    if (!item || !item.value) return;
    const el = targetRef.current;
    if (el && selRef.current) { try { el.setSelectionRange(selRef.current.start, selRef.current.end); } catch { /* */ } }
    setOpen(false);
    onInsert(item.value, item);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(filtered[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
  };

  let lastGroup = '';
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseDown={(e) => e.preventDefault() /* a mező fókusza és kijelölése maradjon */}
        onClick={() => (open ? setOpen(false) : openPicker())}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 cursor-pointer"
        title="Sablonváltozó értékének beszúrása a kurzor helyére"
      >
        <Braces className="w-3 h-3" /> {label}
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          data-esc-self
          style={{ left: pos.left, top: pos.top, width: Math.min(360, window.innerWidth - 16), transform: pos.up ? 'translateY(-100%)' : undefined }}
          className="fixed z-[10001] rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
          onKeyDown={onKey}
        >
          <div className="p-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Keresés: név, kulcs vagy érték…"
              role="combobox"
              aria-controls={listId}
              aria-expanded="true"
              aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              aria-label="Sablonváltozó keresése"
              className="flex-1 bg-transparent text-xs text-slate-900 dark:text-white outline-none"
            />
          </div>
          <ul ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto py-1">
            {loading && !items.length && (
              <li className="px-3 py-4 text-center text-slate-400"><RefreshCw className="w-4 h-4 animate-spin inline" /></li>
            )}
            {!loading && !filtered.length && (
              <li className="px-3 py-4 text-center text-xs text-slate-500">Nincs találat</li>
            )}
            {filtered.map((it, idx) => {
              const header = it.category !== lastGroup ? (lastGroup = it.category, GROUP_TITLE[it.category] || it.category) : null;
              return (
                <React.Fragment key={it.key}>
                  {header && !q && <li role="presentation" className="px-3 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{header}</li>}
                  <li
                    id={`${listId}-${idx}`}
                    data-idx={idx}
                    role="option"
                    aria-selected={idx === active}
                    aria-disabled={!it.value}
                    onMouseEnter={() => setActive(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(it)}
                    className={`px-3 py-1.5 ${it.value ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${idx === active ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{it.label}</span>
                      {!isLiveBlockItem(it) && !isSnippetItem(it) && <span className="text-[10px] font-mono text-slate-400 shrink-0">{it.key}</span>}
                      {isSnippetItem(it) && it.html && <span className="text-[9px] font-semibold px-1 rounded bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 shrink-0">formázott</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{isLiveBlockItem(it) ? 'élő hivatkozás – mindig az aktuális adatot mutatja' : isSnippetItem(it) ? it.value.replace(/\s+/g, ' ').slice(0, 140) : (it.value || '(nincs kitöltve)')}</div>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
          <div className="px-3 py-1.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-400">
            <span>↑↓ választás · Enter beszúrás · Esc</span>
            <button type="button" onClick={() => { setOpen(false); setManageOpen(true); }}
              className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-semibold hover:underline cursor-pointer">
              <Settings2 className="w-3 h-3" /> {canSave ? 'Változók kezelése' : 'Változók megtekintése'}
            </button>
          </div>
        </div>,
        document.body
      )}
      <TemplateVariablesManagerModal isOpen={manageOpen} onClose={() => setManageOpen(false)} tenantId={tenantId} />
    </>
  );
};
